/**
 * Keeps an unfinished /find-teacher questionnaire in the visitor's own browser.
 *
 * The questionnaire is five steps and can be filled in anonymously, so leaving
 * halfway used to cost the whole thing. Nothing goes to the server before
 * submission: unsent answers have no analytical value, and writing anonymous
 * partials into student_requests would bury the drafts we do keep under rows
 * that were never going to be sent.
 *
 * Every function is safe to call when storage is unavailable — private windows,
 * blocked site data and thumbnail capture all make the accessor throw.
 */

const STORAGE_KEY = 'tn.student_request_draft'

// Long enough to cover "I will come back to this at the weekend", short enough
// that we are not asking someone to resume a need they no longer have.
export const EXPIRY_MS = 7 * 24 * 60 * 60 * 1000

function storage() {
  try {
    return globalThis.localStorage || null
  } catch {
    return null
  }
}

/**
 * @param {Object} answers the questionnaire state so far
 * @param {number} step the step the visitor is on
 */
export function save(answers, step) {
  const store = storage()
  if (!store) return

  try {
    store.setItem(STORAGE_KEY, JSON.stringify({ answers, step, savedAt: Date.now() }))
  } catch {
    // A full or locked store is not worth interrupting the questionnaire for.
  }
}

/**
 * @returns {{answers: Object, step: number}|null} null when there is nothing
 *   usable to resume: never stored, expired, or unreadable
 */
export function load() {
  const store = storage()
  if (!store) return null

  let raw
  try {
    raw = store.getItem(STORAGE_KEY)
  } catch {
    return null
  }
  if (!raw) return null

  let parsed
  try {
    parsed = JSON.parse(raw)
  } catch {
    clear()
    return null
  }

  if (!parsed || typeof parsed !== 'object' || !parsed.answers) {
    clear()
    return null
  }

  if (!parsed.savedAt || Date.now() - parsed.savedAt > EXPIRY_MS) {
    clear()
    return null
  }

  return { answers: parsed.answers, step: parsed.step || 0 }
}

export function clear() {
  const store = storage()
  if (!store) return

  try {
    store.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to do: the entry expires on its own.
  }
}
